import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Book } from '../../types';

interface CoverFrontMatterTabProps {
  book: Book;
}

interface LinkCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  status?: string;
  onClick: () => void;
}

function LinkCard({ title, description, icon, status, onClick }: LinkCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-start gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-sm transition-all text-left"
    >
      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium text-gray-900 dark:text-gray-100">{title}</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {description}
        </span>
        {status && (
          <span className="inline-block mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            {status}
          </span>
        )}
      </span>
      <span aria-hidden="true" className="text-gray-400 mt-1">
        →
      </span>
    </button>
  );
}

export default function CoverFrontMatterTab({ book }: CoverFrontMatterTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const frontMatter = book.frontMatterSettings;
  const hasCover = !!book.coverImageUrl;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t(
          'coverFrontMatterTab.description',
          'Customize the artwork and the pages that come before chapter one.'
        )}
      </p>

      <LinkCard
        title={t('coverFrontMatterTab.coverTitle', 'Cover image')}
        description={t(
          'coverFrontMatterTab.coverDescription',
          'Upload or generate the cover artwork. Add text overlays.'
        )}
        status={hasCover ? t('coverFrontMatterTab.statusConfigured', 'Set') : undefined}
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
            />
          </svg>
        }
        onClick={() => navigate(`/books/${book.id}/cover-editor`)}
      />

      <LinkCard
        title={t('coverFrontMatterTab.titlePageTitle', 'Title page')}
        description={t(
          'coverFrontMatterTab.titlePageDescription',
          'Layout, alignment, author name.'
        )}
        status={
          frontMatter?.titlePage?.visible
            ? t('coverFrontMatterTab.statusOn', 'On')
            : t('coverFrontMatterTab.statusOff', 'Off')
        }
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
            />
          </svg>
        }
        onClick={() => navigate(`/books/${book.id}/front-matter/title`)}
      />

      <LinkCard
        title={t('coverFrontMatterTab.copyrightTitle', 'Copyright page')}
        description={t(
          'coverFrontMatterTab.copyrightDescription',
          'Year, rights holder, publisher, disclaimer.'
        )}
        status={
          frontMatter?.copyright?.visible
            ? t('coverFrontMatterTab.statusOn', 'On')
            : t('coverFrontMatterTab.statusOff', 'Off')
        }
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
        onClick={() => navigate(`/books/${book.id}/front-matter/copyright`)}
      />

      <LinkCard
        title={t('coverFrontMatterTab.dedicationTitle', 'Dedication')}
        description={t(
          'coverFrontMatterTab.dedicationDescription',
          'A short note to whoever this book is for.'
        )}
        status={
          frontMatter?.dedication?.visible
            ? t('coverFrontMatterTab.statusOn', 'On')
            : t('coverFrontMatterTab.statusOff', 'Off')
        }
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
            />
          </svg>
        }
        onClick={() => navigate(`/books/${book.id}/front-matter/dedication`)}
      />

      <LinkCard
        title={t('coverFrontMatterTab.epigraphTitle', 'Epigraph')}
        description={t(
          'coverFrontMatterTab.epigraphDescription',
          'A quote that sets the tone before chapter one.'
        )}
        status={
          frontMatter?.epigraph?.visible
            ? t('coverFrontMatterTab.statusOn', 'On')
            : t('coverFrontMatterTab.statusOff', 'Off')
        }
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M9 5H7a2 2 0 00-2 2v3a2 2 0 002 2h2v3a2 2 0 01-2 2H6m13-12h-2a2 2 0 00-2 2v3a2 2 0 002 2h2v3a2 2 0 01-2 2h-1"
            />
          </svg>
        }
        onClick={() => navigate(`/books/${book.id}/front-matter/epigraph`)}
      />
    </motion.div>
  );
}
